/**
 * Meeting Tools → Recording panel.
 *
 * Start / Pause / Continue / Stop student-view session recording.
 * Egress captures the student attending view with Meeting Tools collapsed
 * (same header, stage, and footer control bar).
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Pause, Play, Square, Circle } from "lucide-react";
import {
  continueSessionRecording,
  fetchSessionRecordingStatus,
  pauseSessionRecording,
  startSessionRecording,
  stopSessionRecording,
  type RecordingActionResult,
  type RecordingSegment,
  type RecordingStatus,
} from "./recordingApi";

type SessionRecordingPanelProps = {
  sessionId: string;
  onStatusChange?: (status: RecordingStatus) => void;
  /** Called after Start so the room can collapse Meeting Tools for a full stage. */
  onRecordingStarted?: () => void;
};

const MERGE_POLL_MS = 4000;
const MERGE_POLL_MAX = 30;

function recordingErrorMessage(err: unknown): string {
  const data = (err as { response?: { data?: { detail?: unknown; message?: string } } })
    ?.response?.data;
  const detail = data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail) && detail[0] && typeof detail[0] === "object") {
    const first = detail[0] as { msg?: string };
    if (first.msg) {
      return first.msg;
    }
  }
  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }
  return (err as Error)?.message || "Recording action failed";
}

function statusLabel(status: RecordingStatus): string {
  switch (status) {
    case "recording":
      return "Recording";
    case "paused":
      return "Paused";
    case "stopped":
      return "Stopped";
    case "idle":
      return "Not recording";
    default:
      return status || "Not recording";
  }
}

function isMergePending(result: RecordingActionResult): boolean {
  return Boolean(result.extra?.merge_pending) && !result.extra?.merged;
}

export function SessionRecordingPanel({
  sessionId,
  onStatusChange,
  onRecordingStarted,
}: SessionRecordingPanelProps) {
  const [status, setStatus] = useState<RecordingStatus>("idle");
  const [segmentList, setSegmentList] = useState<RecordingSegment[]>([]);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [primaryUrl, setPrimaryUrl] = useState<string | null>(null);
  const [mergePending, setMergePending] = useState(false);
  const [mergeFailed, setMergeFailed] = useState(false);
  const mergePollCount = useRef(0);
  /** Sync guard so double-clicks can't race past React's async setBusy. */
  const busyRef = useRef(false);
  /** Keep latest callbacks without putting them in effect deps (avoids status spam). */
  const onStatusChangeRef = useRef(onStatusChange);
  const onRecordingStartedRef = useRef(onRecordingStarted);
  const statusRef = useRef<RecordingStatus>("idle");
  onStatusChangeRef.current = onStatusChange;
  onRecordingStartedRef.current = onRecordingStarted;

  const applyResult = useCallback((result: RecordingActionResult) => {
    // ---------------------------------------------------------------
    // step 1: Sync panel state from lkapi recording status / action
    // ---------------------------------------------------------------
    const next = (result.status || "idle") as RecordingStatus;
    const prev = statusRef.current;
    setStatus(next);
    setSegmentList(Array.isArray(result.segments) ? result.segments : []);
    setPrimaryUrl(
      (result.extra && (result.extra.playback_urls as string[] | undefined)?.[0]) ||
        result.url ||
        null,
    );
    setMergePending(isMergePending(result));
    setMergeFailed(Boolean(result.extra?.merge_failed));
    statusRef.current = next;
    // Only notify parent when status actually changes (stops TopBar flicker).
    if (prev !== next) {
      onStatusChangeRef.current?.(next);
    }
  }, []);

  // One status fetch when the panel opens / session changes — not on every parent render.
  useEffect(() => {
    let cancelled = false;
    setReady(false);
    void (async () => {
      try {
        const result = await fetchSessionRecordingStatus(sessionId);
        if (!cancelled) applyResult(result);
      } catch (err: unknown) {
        if (!cancelled) setError(recordingErrorMessage(err));
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, applyResult]);

  // Poll only while a post-stop merge is pending (not while idle / recording).
  useEffect(() => {
    if (!mergePending) {
      mergePollCount.current = 0;
      return;
    }
    let cancelled = false;
    const pollOnce = () => {
      if (cancelled || mergePollCount.current >= MERGE_POLL_MAX) {
        return;
      }
      mergePollCount.current += 1;
      void fetchSessionRecordingStatus(sessionId)
        .then((result) => {
          if (cancelled) return;
          applyResult(result);
        })
        .catch(() => {
          /* keep polling until max attempts */
        });
    };
    // Interval only — initial status already loaded on mount / after Stop.
    const timer = window.setInterval(pollOnce, MERGE_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [mergePending, sessionId, applyResult]);

  const run = async (
    fn: () => Promise<RecordingActionResult>,
    opts?: { notifyStarted?: boolean },
  ) => {
    if (!ready || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await fn();
      applyResult(result);
      // Collapse tools only after an explicit Start — not on status hydrate.
      if (opts?.notifyStarted && (result.status || "idle") === "recording") {
        onRecordingStartedRef.current?.();
      }
    } catch (err: unknown) {
      const message = recordingErrorMessage(err);
      // Status drift (e.g. UI still "Paused" after webhook closed the egress):
      // re-fetch so Continue/Stop/Start match the server and clear a false error.
      if (
        /already (recording|paused)|not paused|cannot continue|cannot pause|no active egress/i.test(
          message,
        )
      ) {
        try {
          applyResult(await fetchSessionRecordingStatus(sessionId));
          setError(null);
          return;
        } catch {
          /* keep original message */
        }
      }
      setError(message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const actionsDisabled = busy || !ready;

  const isRecording = status === "recording";
  const isPaused = status === "paused";
  const canStart = !isRecording && !isPaused;
  const showSingleRecording =
    segmentList.length === 1 ||
    Boolean(primaryUrl && !mergePending && status === "stopped");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 12px",
          borderRadius: 10,
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: isRecording
              ? "#ef4444"
              : isPaused
                ? "#fbbf24"
                : mergePending
                  ? "#38bdf8"
                  : "#64748b",
          }}
        />
        <span style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 600 }}>
          {mergePending ? "Processing recording…" : statusLabel(status)}
        </span>
        {showSingleRecording && !mergePending ? (
          <span style={{ marginLeft: "auto", color: "#64748b", fontSize: 11 }}>
            1 file
          </span>
        ) : null}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {canStart ? (
          <button
            type="button"
            disabled={actionsDisabled}
            aria-busy={busy}
            onClick={() =>
              void run(() => startSessionRecording(sessionId), {
                notifyStarted: true,
              })
            }
            style={btnStyle("#dc2626", actionsDisabled)}
          >
            <Circle size={14} fill="currentColor" />
            Start
          </button>
        ) : null}
        {isRecording ? (
          <button
            type="button"
            disabled={actionsDisabled}
            aria-busy={busy}
            onClick={() => void run(() => pauseSessionRecording(sessionId))}
            style={btnStyle("#b45309", actionsDisabled)}
          >
            <Pause size={14} />
            Pause
          </button>
        ) : null}
        {isPaused ? (
          <button
            type="button"
            disabled={actionsDisabled}
            aria-busy={busy}
            onClick={() => void run(() => continueSessionRecording(sessionId))}
            style={btnStyle("#2563eb", actionsDisabled)}
          >
            <Play size={14} />
            Continue
          </button>
        ) : null}
        {isRecording || isPaused ? (
          <button
            type="button"
            disabled={actionsDisabled}
            aria-busy={busy}
            onClick={() => void run(() => stopSessionRecording(sessionId))}
            style={btnStyle("#475569", actionsDisabled)}
          >
            <Square size={14} />
            Stop
          </button>
        ) : null}
      </div>

      {error ? (
        <p style={{ margin: 0, color: "#fca5a5", fontSize: 12 }}>{error}</p>
      ) : null}

      {mergePending ? (
        <p style={{ margin: 0, color: "#94a3b8", fontSize: 11, lineHeight: 1.45 }}>
          Combining recording parts into one file. This usually takes under a
          minute.
        </p>
      ) : null}

      {mergeFailed && segmentList.length > 1 ? (
        <p style={{ margin: 0, color: "#fbbf24", fontSize: 11, lineHeight: 1.45 }}>
          Could not merge into one file. Individual parts are listed below.
        </p>
      ) : null}

      {segmentList.length > 0 && !mergePending ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: "#e2e8f0" }}>
            Session recording
          </p>
          {segmentList.map((seg, index) => {
            const label = showSingleRecording
              ? `Full recording${seg.status ? ` (${seg.status})` : ""}`
              : `Part ${index + 1}${seg.status ? ` (${seg.status})` : ""}`;
            if (seg.url) {
              return (
                <a
                  key={seg.egress_id || String(index)}
                  href={seg.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "#93c5fd", fontSize: 11, wordBreak: "break-all" }}
                >
                  {label}
                </a>
              );
            }
            const waiting =
              seg.status === "starting" ||
              seg.status === "recording" ||
              seg.status === "ending";
            return (
              <p
                key={seg.egress_id || String(index)}
                style={{ margin: 0, color: "#94a3b8", fontSize: 11 }}
              >
                {label}
                {waiting
                  ? " — file uploads after you stop recording"
                  : " — file not in storage yet"}
              </p>
            );
          })}
        </div>
      ) : primaryUrl && !mergePending ? (
        <p style={{ margin: 0, color: "#64748b", fontSize: 11, wordBreak: "break-all" }}>
          Last file: {primaryUrl}
        </p>
      ) : null}
    </div>
  );
}

function btnStyle(bg: string, disabled = false): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "none",
    borderRadius: 10,
    padding: "8px 14px",
    background: bg,
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
    pointerEvents: disabled ? "none" : "auto",
  };
}
